import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplet, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import type { User } from '../../../shared/contracts';

export default function LockedAccount() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const hasNavigated = useRef(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Poll for unlock status
  useEffect(() => {
    const pollUnlockStatus = async () => {
      try {
        const authToken = localStorage.getItem('auth_token');
        if (!authToken) return;

        const response = await api.get<{ user: User }>('/auth/me', {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        // Account is unlocked!
        if (!response.user?.isLocked && !hasNavigated.current) {
          console.log('[LockedAccount] Account unlocked, redirecting to dashboard');
          hasNavigated.current = true;

          // Clear the interval to stop checking
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

          // Redirect to dashboard
          navigate('/dashboard', { replace: true });
        }
      } catch (error: any) {
        // Handle 403 - account is still locked (expected)
        if (error?.status === 403 && error?.message?.toLowerCase().includes('lock')) {
          console.log('[LockedAccount] Account still locked');
          return;
        }

        // Handle 401 - user deleted or unauthorized
        if (error?.status === 401) {
          console.log('[LockedAccount] User unauthorized (401), signing out');
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          logout();
          return;
        }

        // Silently ignore other errors during polling
        console.log('[LockedAccount] Polling error (will retry):', error?.message?.substring(0, 50));
      }
    };

    // Start polling immediately
    pollUnlockStatus();
    pollIntervalRef.current = setInterval(pollUnlockStatus, 3000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [navigate, logout]);

  const handleLogout = async () => {
    console.log('[LockedAccount] User clicked logout');
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-600 to-cyan-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="bg-white bg-opacity-20 rounded-full p-4 backdrop-blur-sm">
              <Droplet className="h-12 w-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white">AquaFlow</h1>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Account Locked</h2>
        </div>

        {/* Message Card */}
        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-2xl p-8 border border-white border-opacity-20 mb-8">
          <p className="text-white text-center mb-6 leading-relaxed">
            Your account has been locked by your company administrator.
          </p>

          <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-6 border-l-4 border-white">
            <h3 className="font-semibold text-white mb-2">Next Steps:</h3>
            <p className="text-white text-sm leading-relaxed">
              Please contact your company administrator to unlock your account and regain access.
            </p>
          </div>

          {user?.email && (
            <div className="text-center">
              <p className="text-white text-opacity-70 text-sm mb-1">Account Email</p>
              <p className="text-white font-medium break-all">{user.email}</p>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full px-4 py-3 rounded-lg text-cyan-700 bg-white hover:bg-gray-100 transition-colors font-semibold flex items-center justify-center"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </button>

        {/* Status Message */}
        <p className="text-center text-white text-opacity-70 text-xs mt-4">
          You will be automatically redirected when your account is unlocked
        </p>
      </div>
    </div>
  );
}
